import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text } from '@chakra-ui/react'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { MdRestore } from 'react-icons/md'
import { useRouteMatch } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../../hooks/useToast'
import type {
  FileDetailsResponse,
  RestoreFileVersionErrorResponse,
  MLFileDetailRouteParams,
} from '../../../interfaces'
import { MediaLibraryActions, MLContext } from '../../../providers/MLProvider'

interface Props {
  isOpen: boolean
  onClose(): void
  onSuccessClose(): Promise<void>
}

const RestoreFileVersionModal = ({
  isOpen,
  onClose,
  onSuccessClose,
}: Props): React.ReactElement => {
  const {
    params: { versionId, fileId },
  } = useRouteMatch<MLFileDetailRouteParams>()

  const { t } = useTranslation()
  const toast = useToast()

  const [, dispatch] = useContext(MLContext)

  const { response, loading, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const restoreFileVersion = async (): Promise<void> => {
    const res = (await request.put(
      `/api/v2/files/${fileId}/versions/${versionId}/restore`,
    )) as FileDetailsResponse | RestoreFileVersionErrorResponse

    if (request.error) {
      if ([400, 404].includes(response.status)) {
        toast({
          description: (res as RestoreFileVersionErrorResponse).message,
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        toast({
          description: t(
            'error_in_restoring_file_version',
            'Error in restoring file version, please try again after sometime',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }
    } else {
      toast({
        description: t(
          'file_version_restored_successfully',
          'File version restored successfully',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      dispatch({
        type: MediaLibraryActions.UPDATE_FILES,
        payload: [res as FileDetailsResponse],
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={MdRestore}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('restore_file_version', 'Restore file version')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="appBattleshipGrey">
            {t(
              'restore_file_version_description',
              'Restore this version of file to current version.',
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            isDisabled={loading}
            isLoading={loading}
            onClick={async () => {
              await restoreFileVersion()
              await onSuccessClose()
              onClose()
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default RestoreFileVersionModal
